import { AppMasterDBFacade, InputAppClientDBFacade, selectedSubject } from '@monorepo/core-data';
import { Injectable } from '@angular/core';
import { map, Observable, take } from 'rxjs';
import { switchMap } from 'rxjs';
import { ComponentLineInDataProvider } from './component-line-in.data.provider';
import {
  BctxViz,
  TComponentGroupUndoResponseDTO,
  TComponentLineInBundleTransactionDetailsResponse,
  TComponentLineInBundleTransactionResponse,
  TComponentLineInGetTagDetailsResponse,
  TComponentLineInTransactionResponse,
  TComponentRequestDTO,
  TComponentsGroupAddResponseDTO,
  TComponentTagWipInfoResponseDTO,
  TTransactionDetailsResponse,
} from '@monorepo/types';
import { TUserPaginationOption } from '@monorepo/ui';
@Injectable({
  providedIn: 'root',
})
export class ComponentLineInCommonService {
  constructor(
    private appMasterDBFacade: AppMasterDBFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private componentLineInDataProvider: ComponentLineInDataProvider,
  ) {}

  saveComponentTags(tags: TComponentRequestDTO[]): Observable<TComponentsGroupAddResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([appDetails, subjectKey, selectedSubject]) => {
        return this.componentLineInDataProvider.saveComponentTags({
          appDetails: {
            an: appDetails.an,
            appType: appDetails.appType,
            appVersion: appDetails.appVersion,
            deviceId: appDetails.deviceId,
            dnm: appDetails.dnm,
            dnr: appDetails.dnr,
            dnrk: appDetails.dnrk,
            hsk: selectedSubject?.hsk || '',
            qtyValidation: appDetails.qtyValidation,
            shift: appDetails.shift,
            shiftDn: appDetails.shiftDn,
            snm: appDetails.snm,
            subject: selectedSubject?.subject || '',
            subjectKey: subjectKey,
            urId: appDetails.urId,
            urNm: appDetails.urNm,
            wipType: appDetails.wipType,
          },
          subjectKey,
          componentInfoList: tags,
        });
      }),
    );
  }

  fetchTransactions(
    filters: TComponentLineInTransactionResponse['supportedFilters'],
    userPaginationOption: TUserPaginationOption,
  ): Observable<TComponentLineInTransactionResponse> {
    return this.appMasterDBFacade.getBuzKeys().pipe(
      take(1),
      switchMap((buzKeys) => {
        return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
          take(1),
          map(({ subjectKey, selectedSubject }) => [buzKeys, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([buzKeys, subjectKey, selectedSubject]) => {
        return this.componentLineInDataProvider.fetchTransactionLog({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          filters,
          filterFields: [
            ...this.getAvailableBctxKeys(buzKeys),
            'component_display_tag_id',
            'component_type',
            'bundle_display_tag_id',
          ],
          pageNo: userPaginationOption.pageNo,
          pageSize: userPaginationOption.pageSize,
        });
      }),
    );
  }

  getTransactionDetails(componentTagId: string): Observable<TTransactionDetailsResponse> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.componentLineInDataProvider.getTransactionDetails({
          subjectKey,
          subject: selectedSubject?.subject || '',
          domRole: selectedSubject?.gatek || '',
          componentTagId: componentTagId,
        });
      }),
    );
  }

  undoComponentTags(tag: TComponentRequestDTO): Observable<TComponentGroupUndoResponseDTO> {
    return this.inputAppClientDBFacade.getSubjectKeyAndSelectedSubject().pipe(
      take(1),
      switchMap(({ subjectKey, selectedSubject }) => {
        return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
          take(1),
          map((appDetails) => [appDetails, subjectKey, selectedSubject] as const),
        );
      }),
      switchMap(([appDetails, subjectKey, selectedSubject]) => {
        return this.componentLineInDataProvider.undoComponentTags({
          appDetails: {
            an: appDetails.an,
            appType: appDetails.appType,
            appVersion: appDetails.appVersion,
            deviceId: appDetails.deviceId,
            dnm: appDetails.dnm,
            dnr: appDetails.dnr,
            dnrk: appDetails.dnrk,
            hsk: selectedSubject?.hsk || '',
            qtyValidation: appDetails.qtyValidation,
            shift: appDetails.shift,
            shiftDn: appDetails.shiftDn,
            snm: appDetails.snm,
            subject: selectedSubject?.subject || '',
            subjectKey: subjectKey,
            urId: appDetails.urId,
            urNm: appDetails.urNm,
            wipType: appDetails.wipType,
          },
          subjectKey: subjectKey,
          componentInfo: tag,
        });
      }),
    );
  }

  getAvailableBctxKeys(buzKeys: string[]) {
    const bctxKeys = ['buyer', 'style', 'po', 'size', 'product_type', 'product_category', 'order_ref', 'color'];
    return bctxKeys.filter((key) => buzKeys.includes(key));
  }

  getAvailableBctxViz(bctxViz: BctxViz[]) {
    const bctxKeys = ['buyer', 'style', 'po', 'size', 'product_type', 'product_category', 'order_ref', 'color'];
    return bctxViz.filter((key) => bctxKeys.includes(key.k));
  }

  getDisplayName(bctxViz: BctxViz[], key: string) {
    return bctxViz.find((bctx) => bctx.k === key)?.d || '_';
  }
}
